<template>
  <b-modal
    id="print-modal"
    v-model="showModal"
    :title="modalTitle"
    :size="modalSize"
    hide-footer
    @hidden="onModalHidden"
  >
    <div v-if="isContentLoading" class="text-center py-3">
      <b-spinner label="Loading..." />
    </div>

    <div v-else-if="printType === 'invoice'">
      <p v-if="invoiceError" class="text-danger mb-2">{{ invoiceError }}</p>
      <iframe
        v-else-if="invoicePdfUrl"
        ref="invoiceFrameRef"
        :src="invoicePdfUrl"
        class="invoice-preview-frame"
        title="Invoice preview"
      />

      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="closeModal">
          Close
        </b-button>
        <b-button
          variant="primary"
          :disabled="isPrinting || !invoicePdfUrl"
          @click="onPrint"
        >
          <b-spinner v-if="isPrinting" small class="mr-50" />
          Print
        </b-button>
      </div>
    </div>

    <div v-else-if="sale">
      <div v-if="printType === 'label'" id="print_label" class="print-sheet print-label-sheet">
        <p class="label-title">Shipping Label</p>
        <p class="label-order-no">Order #{{ sale.id }}</p>
        <hr class="label-divider">

        <div class="label-section">
          <div class="label-section-title">Ship to</div>
          <div>{{ sale.customer_name || '—' }}</div>
          <div v-if="deliveryAddressLines.length">
            <div v-for="(line, index) in deliveryAddressLines" :key="`addr-${index}`">
              {{ line }}
            </div>
          </div>
          <div v-if="sale.phone">{{ sale.phone }}</div>
        </div>

        <hr class="label-divider">

        <div class="label-section label-meta">
          <div><strong>Date:</strong> {{ sale.date }}</div>
          <div><strong>Type:</strong> {{ sale.pickup ? 'Pickup' : 'Delivery' }}</div>
          <div v-if="itemSummary"><strong>Items:</strong> {{ itemSummary }}</div>
        </div>

        <div v-if="companyName" class="label-section label-meta">
          <div class="label-section-title">From</div>
          <div>{{ companyName }}</div>
          <div v-if="companyPhone">{{ companyPhone }}</div>
        </div>
      </div>

      <div v-else-if="printType === 'packing_slip'" id="print_packing_slip" class="print-sheet print-packing-slip-sheet">
        <h1 class="packing-slip-title">
          Packing Slip for Order #{{ sale.id }}
        </h1>

        <div class="packing-slip-row packing-slip-row-company">
          <div class="packing-slip-col packing-slip-col-logo">
            <img
              v-if="companyPicture"
              class="packing-slip-logo"
              :src="companyPicture"
              :alt="`${companyName || 'Company'} logo`"
            >
          </div>
          <div class="packing-slip-col packing-slip-col-company">
            <div v-if="companyName" class="packing-slip-company-name">{{ companyName }}</div>
            <div v-if="hasCompanyAddressDisplayLines" class="packing-slip-company-address">
              <div v-for="(line, index) in companyAddressDisplayLines" :key="`company-addr-${index}`">
                {{ line }}
              </div>
            </div>
            <div v-if="companyPhone" class="packing-slip-company-phone">{{ companyPhone }}</div>
          </div>
        </div>

        <div class="packing-slip-row packing-slip-row-details">
          <div class="packing-slip-col packing-slip-col-ship-to">
            <div class="packing-slip-section-heading">Ship to</div>
            <div>{{ sale.customer_name || '—' }}</div>
            <div v-if="hasPackingSlipShipToLines">
              <div v-for="(line, index) in packingSlipShipToLines" :key="`slip-addr-${index}`">
                {{ line }}
              </div>
            </div>
            <div v-if="sale.phone">{{ sale.phone }}</div>
          </div>

          <div class="packing-slip-col packing-slip-col-order-meta">
            <div class="packing-slip-meta-line">
              <span class="packing-slip-meta-label">Order Id:</span> #{{ sale.id }}
            </div>
            <div class="packing-slip-meta-line">
              <span class="packing-slip-meta-label">Order Date:</span> {{ packingSlipOrderDate }}
            </div>
            <div v-if="shippedVia" class="packing-slip-meta-line">
              <span class="packing-slip-meta-label">Shipping Carrier:</span> {{ shippedVia }}
            </div>
            <div v-if="trackingNumber" class="packing-slip-meta-line">
              Tracking number: {{ trackingNumber }}
            </div>
          </div>
        </div>

        <table class="packing-slip-items">
          <thead>
            <tr>
              <th class="packing-slip-col-item">Item</th>
              <th class="packing-slip-col-qty">Quantity</th>
              <th class="packing-slip-col-price">Price</th>
              <th class="packing-slip-col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in packingSlipLineItems" :key="`slip-item-${index}`">
              <td class="packing-slip-col-item">
                <div>{{ item.name }}</div>
                <div v-if="item.sku" class="packing-slip-item-sku">{{ item.sku }}</div>
              </td>
              <td class="packing-slip-col-qty">{{ item.quantity }}</td>
              <td class="packing-slip-col-price">{{ formatMoney(item.price) }}</td>
              <td class="packing-slip-col-total">{{ formatMoney(item.lineTotal) }}</td>
            </tr>
            <tr v-if="!hasPackingSlipLineItems">
              <td colspan="4" class="packing-slip-empty">No line items</td>
            </tr>
          </tbody>
        </table>

        <div class="packing-slip-row packing-slip-row-totals">
          <div class="packing-slip-col packing-slip-col-totals-spacer" />
          <div class="packing-slip-col packing-slip-col-totals">
            <div class="packing-slip-total-line">
              <span>Subtotal:</span>
              <span>{{ formatMoney(sale.subtotal) }}</span>
            </div>
            <div
              v-if="Number(sale.tax_total || 0) !== 0"
              class="packing-slip-total-line"
            >
              <span>Tax:</span>
              <span>{{ formatMoney(sale.tax_total) }}</span>
            </div>
            <div class="packing-slip-total-line">
              <span>Shipping:</span>
              <span>{{ formatMoney(sale.shipping) }}</span>
            </div>
            <div class="packing-slip-total-line packing-slip-grand-total">
              <span>Total:</span>
              <span>{{ formatTotalWithCurrency(sale.total) }}</span>
            </div>
          </div>
        </div>

        <p v-if="sale.customer_note" class="packing-slip-note">
          <strong>Note:</strong> {{ sale.customer_note }}
        </p>
      </div>

      <div v-else-if="printType === 'delivery_note'" id="print_delivery_note" class="print-sheet print-delivery-note-sheet">
        <div class="delivery-note-banner">Delivery Note</div>

        <div class="delivery-note-info-box">
          <div class="delivery-note-company-col">
            <div class="delivery-note-company-main">
              <img
                v-if="companyPicture"
                class="delivery-note-logo"
                :src="companyPicture"
                :alt="`${companyName || 'Company'} logo`"
              >
              <div class="delivery-note-company-text">
                <div v-if="companyName" class="delivery-note-company-name">{{ companyName }}</div>
                <div v-if="hasCompanyAddressDisplayLines" class="delivery-note-company-address">
                  <div v-for="(line, index) in companyAddressDisplayLines" :key="`dn-company-addr-${index}`">
                    {{ line }}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="companyPhone" class="delivery-note-company-phone">{{ companyPhone }}</div>
            <div v-if="companyEmail" class="delivery-note-company-website">{{ companyEmail }}</div>
          </div>

          <div class="delivery-note-details-col">
            <div class="delivery-note-detail-row">
              <span class="delivery-note-detail-label">To:</span>
              <span class="delivery-note-detail-value">{{ sale.customer_name || '—' }}</span>
            </div>
            <div class="delivery-note-detail-row">
              <span class="delivery-note-detail-label">Address:</span>
              <span class="delivery-note-detail-value">{{ deliveryNoteAddressText || '—' }}</span>
            </div>
            <div class="delivery-note-detail-row">
              <span class="delivery-note-detail-label">Order Number:</span>
              <span class="delivery-note-detail-value">#{{ sale.id }}</span>
            </div>
            <div class="delivery-note-detail-row">
              <span class="delivery-note-detail-label">Order Date:</span>
              <span class="delivery-note-detail-value">{{ packingSlipOrderDate }}</span>
            </div>
            <div v-if="deliveryNoteShippingDate" class="delivery-note-detail-row">
              <span class="delivery-note-detail-label">Shipping Date:</span>
              <span class="delivery-note-detail-value">{{ deliveryNoteShippingDate }}</span>
            </div>
          </div>
        </div>

        <table class="delivery-note-items">
          <thead>
            <tr>
              <th class="delivery-note-col-yn">Y/N</th>
              <th class="delivery-note-col-item">Item</th>
              <th class="delivery-note-col-qty">Quantity</th>
              <th class="delivery-note-col-price">Price</th>
              <th class="delivery-note-col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in deliveryNoteTableRows" :key="`dn-item-${row.index}`">
              <td class="delivery-note-col-yn">{{ row.index }}</td>
              <td class="delivery-note-col-item">{{ row.name }}</td>
              <td class="delivery-note-col-qty">{{ row.quantity }}</td>
              <td class="delivery-note-col-price">{{ formatMoney(row.price) }}</td>
              <td class="delivery-note-col-total">{{ formatMoney(row.lineTotal) }}</td>
            </tr>
            <tr v-if="!hasPackingSlipLineItems">
              <td colspan="5" class="delivery-note-empty">No line items</td>
            </tr>
          </tbody>
        </table>

        <div class="delivery-note-totals">
          <div class="delivery-note-total-line">
            <span>Subtotal:</span>
            <span>{{ formatMoney(sale.subtotal) }}</span>
          </div>
          <div
            v-if="Number(sale.tax_total || 0) !== 0"
            class="delivery-note-total-line"
          >
            <span>Tax:</span>
            <span>{{ formatMoney(sale.tax_total) }}</span>
          </div>
          <div class="delivery-note-total-line">
            <span>Shipping:</span>
            <span>{{ formatMoney(sale.shipping) }}</span>
          </div>
          <div class="delivery-note-total-line delivery-note-grand-total">
            <span>Total:</span>
            <span>{{ formatTotalWithCurrency(sale.total) }}</span>
          </div>
        </div>
      </div>

      <div v-else-if="printType === 'message'" id="print_message" class="print-sheet print-customer-sheet">
        <div class="text-center mb-2">
          <b-img
            v-if="appLogoImage"
            class="print-logo img-fluid"
            :src="appLogoImage"
            :alt="`Logo order ${sale.id}`"
          />
        </div>
        <p><em>{{ message || '—' }}</em></p>
        <b-form-group class="mt-2">
          <b-form-textarea
            v-model="message"
            placeholder="Type the message"
            rows="3"
            max-rows="6"
          />
        </b-form-group>
      </div>

      <p v-if="printType === 'label'" class="text-muted small mb-1">
        In Chrome, turn off <strong>Headers and footers</strong> in the print dialog for a clean label.
      </p>

      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="closeModal">
          Close
        </b-button>
        <b-button variant="primary" :disabled="isPrinting" @click="onPrint">
          <b-spinner v-if="isPrinting" small class="mr-50" />
          Print
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BFormGroup,
  BFormTextarea,
  BImg,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import { toastFailure } from '@/libs/toastification'
import { sweetWarning } from '@/libs/sweet-alerts'
import {
  getCurrencySymbol,
  moneyRound,
  normalizeImageUrl,
  hasCompanyPrintAddress,
  companyPrintAddressMissingTitle,
  COMPANY_PRINT_ADDRESS_MISSING_BODY,
} from '@/libs/utils'
import { formatCivilDate } from '@/libs/companyTime'
import { $themeConfig } from '@themeConfig'
import '@/assets/css/print.css'

const props = defineProps({
  saleId: {
    type: [Number, String],
    default: null,
  },
  printType: {
    type: String,
    default: null,
  },
  clickDate: {
    type: Date,
    default: null,
  },
})

const showModal = ref(false)
const isContentLoading = ref(false)
const isPrinting = ref(false)
const message = ref('')
const invoicePdfUrl = ref('')
const invoiceError = ref('')
const invoiceFrameRef = ref(null)
const { appLogoImage } = $themeConfig.app

const store = useStore()
const instance = getCurrentInstance()
const htmlToPaper = instance?.appContext.config.globalProperties.$htmlToPaper

const sale = computed(() => store.getters['sales/sale'] || store.state.sales?.sale || null)

const modalTitle = computed(() => {
  if (props.printType === 'label') {
    return 'Print shipping label'
  }
  if (props.printType === 'packing_slip') {
    return 'Print packing slip'
  }
  if (props.printType === 'invoice') {
    return 'Print invoice'
  }
  if (props.printType === 'delivery_note') {
    return 'Print delivery note'
  }
  if (props.printType === 'message') {
    return 'Print message'
  }
  return 'Print'
})

const modalSize = computed(() => (props.printType === 'invoice' ? 'xl' : 'lg'))

const companyName = computed(() => sale.value?.company?.name || store.getters['companies/companySettings']?.name || '')
const companyPhone = computed(() => sale.value?.company?.phone || store.getters['companies/companySettings']?.phone || '')
const companyEmail = computed(() => sale.value?.company?.email || store.getters['companies/companySettings']?.email || '')

const companyPicture = computed(() => {
  const picture = sale.value?.company?.picture
    || store.getters['companies/companySettings']?.picture

  if (!picture || String(picture).trim() === '') {
    return ''
  }

  return normalizeImageUrl(picture)
})

const resolveCompanyCountryName = (company) => {
  if (!company) {
    return ''
  }

  if (company.country && typeof company.country === 'string') {
    return company.country
  }

  if (company.country?.long_name) {
    return company.country.long_name
  }

  if (company.country_short_name) {
    return String(company.country_short_name).toUpperCase()
  }

  const countryId = company.country_id
  if (countryId == null) {
    return ''
  }

  const countries = store.getters['companies/countries'] || []
  const match = countries.find((item) => Number(item.id) === Number(countryId))
  return match?.long_name || ''
}

const companyAddressDisplayLines = computed(() => {
  const bookingCompany = sale.value?.company || {}
  const settingsCompany = store.getters['companies/companySettings'] || {}
  const company = {
    ...settingsCompany,
    ...bookingCompany,
  }

  if (!company || (!company.address_line_1 && !company.city && !company.zip_code && !company.country_id && !company.country)) {
    return []
  }

  const cityRegionZip = [company.city, company.region, company.zip_code]
    .filter(Boolean)
    .join(', ')

  return [
    [company.address_line_1, company.address_line_2].filter(Boolean).join(' '),
    cityRegionZip,
    resolveCompanyCountryName(company) || resolveCompanyCountryName(settingsCompany),
  ].filter(Boolean)
})

const hasCompanyAddressDisplayLines = computed(() => companyAddressDisplayLines.value.length > 0)

const deliveryAddress = computed(() => {
  const current = sale.value
  if (!current) {
    return null
  }

  if (current.address) {
    return current.address
  }

  const addresses = current.user?.addresses
  if (Array.isArray(addresses) && addresses.length) {
    return addresses[0]
  }

  return null
})

const packingSlipShipToLines = computed(() => {
  const address = deliveryAddress.value
  if (!address) {
    return []
  }

  return [
    address.address_line_1,
    address.address_line_2,
    [address.city, address.region, address.zip_code].filter(Boolean).join(', '),
    address.country || (address.country_short_name ? String(address.country_short_name).toUpperCase() : ''),
  ].filter(Boolean)
})

const hasPackingSlipShipToLines = computed(() => packingSlipShipToLines.value.length > 0)

const latestShipping = computed(() => {
  const shippings = sale.value?.shippings
  if (!Array.isArray(shippings) || !shippings.length) {
    return null
  }

  return shippings[0]
})

const shippedVia = computed(() => latestShipping.value?.carrier_name || '')
const trackingNumber = computed(() => latestShipping.value?.tracking_id || '')

const packingSlipOrderDate = computed(() => {
  const rawDate = sale.value?.date
  if (!rawDate) {
    return '—'
  }

  return formatCivilDate(rawDate) || rawDate
})

function formatMoney(value) {
  const symbol = sale.value?.company?.currency_symbol || getCurrencySymbol()
  return `${symbol} ${moneyRound(value).toFixed(2)}`
}

function formatTotalWithCurrency(value) {
  const code = sale.value?.company?.currency_code || 'USD'
  return `${code} ${moneyRound(value).toFixed(2)}`
}

const deliveryAddressLines = computed(() => {
  const address = deliveryAddress.value
  if (!address) {
    return []
  }

  return [
    address.address_line_1,
    address.address_line_2,
    [address.city, address.region].filter(Boolean).join(', '),
    address.zip_code,
    address.country || (address.country_short_name ? String(address.country_short_name).toUpperCase() : ''),
  ].filter(Boolean)
})

const itemSummary = computed(() => {
  const products = sale.value?.products
  if (!Array.isArray(products) || !products.length) {
    return ''
  }

  const totalQty = products.reduce((sum, item) => sum + Number(item.items_quantity || 0), 0)
  return `${totalQty} item${totalQty === 1 ? '' : 's'}`
})

const packingSlipLineItems = computed(() => {
  const current = sale.value
  if (!current) {
    return []
  }

  const products = current.products
  if (Array.isArray(products) && products.length) {
    return products.map((item) => {
      const quantity = Number(item.items_quantity || 0)
      const price = Number(item.price || 0)

      return {
        name: item.name || 'Item',
        sku: item.sku || '',
        quantity,
        price,
        lineTotal: price * quantity,
      }
    })
  }

  if (current.unit || current.package_name) {
    const subtotal = Number(current.subtotal || current.total || 0)

    return [{
      name: [current.unit, current.package_name].filter(Boolean).join(' — ') || 'Booking',
      sku: '',
      quantity: 1,
      price: subtotal,
      lineTotal: subtotal,
    }]
  }

  return []
})

const hasPackingSlipLineItems = computed(() => packingSlipLineItems.value.length > 0)

const deliveryNoteAddressText = computed(() => {
  const address = sale.value?.address
  if (!address) {
    return ''
  }

  const lines = [
    address.address_line_1,
    address.address_line_2,
    [address.city, address.region, address.zip_code].filter(Boolean).join(', '),
    address.country || address.country_short_name,
  ].filter(Boolean)

  return lines.join(', ')
})

const deliveryNoteShippingDate = computed(() => {
  const shippingDate = latestShipping.value?.shipping_date
  if (!shippingDate) {
    return ''
  }

  return formatCivilDate(shippingDate) || shippingDate
})

const deliveryNoteTableRows = computed(() => {
  return packingSlipLineItems.value.map((item, index) => ({
    index: index + 1,
    name: item.name,
    quantity: item.quantity,
    price: item.price,
    lineTotal: item.lineTotal,
  }))
})

const printElementId = computed(() => {
  if (!props.printType) {
    return null
  }
  return `print_${props.printType}`
})

watch(
  () => props.clickDate,
  async (clickDate) => {
    if (!clickDate || !props.saleId || !props.printType) {
      return
    }

    const addressRequiredPrints = ['invoice', 'packing_slip', 'delivery_note']
    if (addressRequiredPrints.includes(props.printType)) {
      let company = store.getters['companies/companySettings']
      if (!company) {
        try {
          company = await store.dispatch('companies/loadCompanySettings')
        } catch (error) {
          company = null
        }
      }

      if (!hasCompanyPrintAddress(company)) {
        sweetWarning(
          companyPrintAddressMissingTitle(props.printType),
          COMPANY_PRINT_ADDRESS_MISSING_BODY,
        )
        return
      }
    }

    isContentLoading.value = true
    showModal.value = true

    try {
      if (props.printType === 'invoice') {
        await loadInvoicePdf()
      } else {
        await Promise.all([
          store.dispatch('sales/saleDetails', props.saleId),
          store.getters['companies/companySettings']
            ? Promise.resolve()
            : store.dispatch('companies/loadCompanySettings').catch(() => {}),
          (store.getters['companies/countries'] || []).length
            ? Promise.resolve()
            : store.dispatch('companies/loadCountries').catch(() => {}),
        ])
      }
    } catch (error) {
      if (props.printType === 'invoice') {
        toastFailure(invoiceError.value || 'Cannot generate invoice.')
      } else {
        showModal.value = false
        toastFailure('Cannot load order details for printing.')
      }
    } finally {
      isContentLoading.value = false
    }
  },
)

function revokeInvoicePdfUrl() {
  if (!invoicePdfUrl.value) {
    return
  }

  URL.revokeObjectURL(invoicePdfUrl.value)
  invoicePdfUrl.value = ''
}

async function parseBlobError(data) {
  try {
    const text = await data.text()
    const payload = JSON.parse(text)

    return payload.message || 'Cannot generate invoice.'
  } catch (error) {
    return 'Cannot generate invoice.'
  }
}

async function loadInvoicePdf() {
  revokeInvoicePdfUrl()
  invoiceError.value = ''

  try {
    const response = await store.dispatch('sales/printInvoice', props.saleId)
    const contentType = response.headers['content-type'] || ''

    if (contentType.includes('application/json')) {
      const message = await parseBlobError(response.data)
      invoiceError.value = message
      throw new Error(message)
    }

    invoicePdfUrl.value = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
  } catch (error) {
    if (!invoiceError.value) {
      if (error.response?.data instanceof Blob) {
        invoiceError.value = await parseBlobError(error.response.data)
      } else {
        invoiceError.value = error.response?.data?.message || error.message || 'Cannot generate invoice.'
      }
    }

    throw error
  }
}

function closeModal() {
  showModal.value = false
}

function onModalHidden() {
  isContentLoading.value = false
  isPrinting.value = false
  revokeInvoicePdfUrl()
  invoiceError.value = ''
}

async function onPrint() {
  if (props.printType === 'invoice') {
    if (!invoicePdfUrl.value) {
      toastFailure('Invoice is not ready to print.')
      return
    }

    isPrinting.value = true

    try {
      const frame = invoiceFrameRef.value
      if (!frame?.contentWindow) {
        toastFailure('Invoice preview is not ready.')
        return
      }

      frame.contentWindow.focus()
      frame.contentWindow.print()
    } catch (error) {
      toastFailure('Cannot open print dialog. Allow pop-ups and try again.')
    } finally {
      isPrinting.value = false
    }

    return
  }

  if (!htmlToPaper || !printElementId.value) {
    toastFailure('Print is not available.')
    return
  }

  isPrinting.value = true

  try {
    await new Promise((resolve) => {
      htmlToPaper(printElementId.value, {}, resolve)
    })
  } catch (error) {
    toastFailure('Cannot open print preview. Allow pop-ups and try again.')
  } finally {
    isPrinting.value = false
  }
}
</script>

<style scoped>
.print-label-sheet {
  border: 1px dashed #bbb;
  margin: 0 auto;
  max-width: 100%;
}

.invoice-preview-frame {
  border: 1px solid #ddd;
  height: 70vh;
  width: 100%;
}
</style>
